import { HttpClient } from '@/libs/http'
import { nextTick, ref, type Component, type Ref } from 'vue'
import { defineStore } from 'pinia'
import { env } from '@/libs/utils/env'
import { UserOutlined } from '@ant-design/icons-vue'
import { WebSocketClient } from '@/libs/websocket'
import { sleep } from '@/libs/utils/sleep'
import type { FetchError } from '@/libs/errors'
import { get_config } from '@/config'

const config = get_config()

export enum Actor {
  USER = 'user',
  ASSISTANT = 'assistant',
}

const avatars = {
  [Actor.ASSISTANT]: () => <a-avatar>AI</a-avatar>,
  [Actor.USER]: () => (
    <a-avatar>
      <UserOutlined />
    </a-avatar>
  ),
}

export const ActorToAuthor = {
  [Actor.ASSISTANT]: 'Assistant',
  [Actor.USER]: 'You',
} as const

export type Message = {
  id: string
  key: string
  actor: Actor
  avatar: Component
  content: string
  created_at: Date
  is_loading: boolean
  is_cancelled?: boolean
}

export type Thread = {
  id: string
  messages: Message
}

const LOADING_MESSAGE = 'Processing...'

export const useAssistantStore = defineStore('assistant', () => {
  const is_busy = ref(false)
  const is_initialized = ref(false)
  const is_connecting = ref(false)
  const is_connected = ref(false)
  const error_message = ref('')
  const current_request_id = ref('')
  const name = ref('AI')
  const messages = ref<Ref<Message>[]>([])
  let token = ''
  const client = new HttpClient({
    serviceName: 'assistant_api',
    baseUrl: config.assistant.api,
  })

  let last_message: Ref<Message> | undefined = append_message({
    content:
      'Hello! How can I assist you today with your queries related to the Sharjah Electricity, Water, and Gas Authority (SEWA)?',
    actor: Actor.ASSISTANT,
    is_loading: false,
  })

  async function reconnect() {
    is_connecting.value = true
    const success = await init_session()
    if (success) {
      socket.reconnect()
    }
  }

  const client_key = ref('')
  async function set_client_key(value: string) {
    client_key.value = value
    return init_session()
  }

  async function init_session() {
    try {
      const result = client.get({ path: '/ai/init', headers: { 'Client-Key': client_key.value } })
      const response = await result.response()
      const data = await response.json()
      if (data.success) {
        token = data.token
        is_connecting.value = true
        socket.token = token
        socket.connect()
        return true
        // is_initialized.value = true
      } else {
        error_message.value = data.message
      }
    } catch (e) {
      const error = e as FetchError
      if ('metadata' in error) {
        const { metadata } = error
        const text = await metadata?.response?.text()
        let data = { message: 'Unknown error' }
        try {
          data = JSON.parse(text)
        } catch (e) {
          data = { message: text }
        }
        error_message.value = data.message
      } else {
        error_message.value = (error as Error).message
      }
    }
    return false
  }

  const socket = new WebSocketClient()
  let start_time = 0
  socket.addEventListener('delta', (event: any) => {
    if (!last_message) {
      last_message = append_message({ content: '', actor: Actor.ASSISTANT, is_loading: true })
    }
    update_message({
      message: last_message,
      content: event.data,
      clear: last_message.value.content === LOADING_MESSAGE,
    })
  })
  socket.addEventListener('open', (event: any) => {
    // is_connecting.value = false
    // is_connected.value = true
    // if (!is_initialized.value) {
    //   is_initialized.value = true
    // }
  })
  socket.addEventListener('connected', (event: any) => {
    is_connecting.value = false
    is_connected.value = true
    if (!is_initialized.value) {
      is_initialized.value = true
    }
  })
  socket.addEventListener('close', (event: any) => {
    is_connected.value = false
    is_connecting.value = false
  })
  socket.addEventListener('error', (event: any) => {
    error_message.value = event.data
    is_connecting.value = false
    const duration = performance.now() - start_time
    console.info('duration: ', duration)
    if (last_message) {
      last_message.value.is_loading = false
      update_message({
        message: last_message,
        content: `Error: ${event.data}`,
        clear: last_message.value.content === LOADING_MESSAGE,
      })
    }
    last_message = undefined
    is_busy.value = false
  })
  socket.addEventListener('completed', () => {
    const duration = performance.now() - start_time
    console.info('duration: ', duration)
    if (last_message) {
      last_message.value.is_loading = false
    }
    last_message = undefined
    is_busy.value = false
  })
  socket.addEventListener('canceled', (event: any) => {
    const message = messages.value.find(({ value: { id } }) => id === event.id)
    if (message) {
      remove_message(message)
    }
  })

  function remove_message(message: Ref<Message>) {
    if (message) {
      const index = messages.value.indexOf(message)
      const question = messages.value[index - 1]
      question.value.is_cancelled = true
      messages.value.splice(index, 1)
    }
  }

  type UpdateMessageArgs = {
    message: Ref<Message>
    content: string
    is_loading?: boolean
    clear?: boolean
  }
  function update_message({
    message,
    content,
    is_loading = false,
    clear = false,
  }: UpdateMessageArgs) {
    if (message) {
      if (clear) {
        message.value.content = content
      } else {
        message.value.content += content
      }
      message.value.is_loading = is_loading
    }
  }

  type AppendMessageArgs = {
    content: string
    actor: Actor
    is_loading?: boolean
  }
  function append_message({ content, actor, is_loading = false }: AppendMessageArgs) {
    const id = (messages.value.length + 1).toString()
    const message = ref({
      id,
      key: id,
      actor,
      avatar: avatars[actor],
      content,
      created_at: new Date(),
      is_loading,
    })
    messages.value.push(message)
    return message
  }

  async function cancel_last_message() {
    if (last_message) {
      cancel_message(last_message)
    }
  }
  async function cancel_message(message?: Ref<Message>) {
    if (message) {
      remove_message(message)
      is_busy.value = false
      if (!current_request_id.value) {
        console.error('There is no request in flight!')
        return
      }
      if (socket.is_connected) {
        socket.send(JSON.stringify({ event: 'cancel', request_id: current_request_id.value }))
      } else {
        const result = await client.post({
          path: '/ai/ask/cancel',
          headers: {
            'Client-Key': client_key.value,
          },
          body: {
            request_id: current_request_id.value,
          },
        })
        const data = await (await result.response()).json()
        if (data.success) {
          // remove_message(message)
        }
      }
      current_request_id.value = ''
    }
  }

  async function send_message(question: string) {
    start_time = performance.now()
    is_busy.value = true
    console.log(`is_busy: ${is_busy.value}`)
    append_message({ content: question, actor: Actor.USER })
    const message = append_message({
      content: '',
      actor: Actor.ASSISTANT,
      is_loading: true,
    })
    last_message = message
    if (socket.is_connected) {
      socket.send(JSON.stringify({ event: 'question', question }))
    } else {
      const result = await client.post({
        path: '/ai/ask',
        headers: {
          'Client-Key': client_key.value,
        },
        body: {
          question,
        },
      })
      const data = await (await result.response()).json()
      append_message({ content: data.messages[0], actor: Actor.ASSISTANT, is_loading: false })
      is_busy.value = false
    }
  }
  return {
    is_busy,
    is_connected,
    is_connecting,
    name,
    messages,
    error_message,
    client_key,
    is_initialized,
    set_client_key,
    reconnect,
    send_message,
    append_message,
    cancel_message,
    cancel_last_message,
  }
})
